import { PaywallApi, ProviderInfo } from '@accedo/one-plugin-sdk';
import { RefObject } from 'react';

interface BasePaywallApi {
  hasOwnUi: () => boolean;
  getProviderInfo: () => Promise<ProviderInfo>;
}

// PaywallApi safe for use in the client app. Has removed obsolete methods.
// You might want to get rid of it and just use PaywallApi directly when
// plugins have obsolete methods removed.
export type ClientPaywallApi = Omit<PaywallApi, 'updateRenewals'> &
  BasePaywallApi;

// Sometimes when the API ref is updating, ref.current can be null for a short while.
// I'm still not sure why it does that. Further investigation needed.
// In the meantime, I will retry on the next tick, maximum this many times.
const MAX_API_RETRIES = 5;

const cantCallError = new Error("Can't call API");

export const retryApi = async (
  apiRef: RefObject<PaywallApi>,
  counter: number = 0,
  maxRetries: number = MAX_API_RETRIES
): Promise<PaywallApi> => {
  if (apiRef.current) {
    return apiRef.current;
  }

  if (counter > maxRetries) {
    throw new Error('API timeout');
  }

  return new Promise((resolve) => {
    window.setTimeout(
      () => resolve(retryApi(apiRef, counter + 1, maxRetries)),
      100
    );
  });
};

export const getPaywallApiFromRef = async (
  apiRef: RefObject<PaywallApi>
): Promise<ClientPaywallApi> => {
  try {
    const {
      cancelSubscription,
      updateRenewals,
      restoreSubscription,
      changePlan,
      moveSubscription,
      ...api
    } = await retryApi(apiRef);

    const clientApi = api as ClientPaywallApi;

    // Place for custom implementations of given API methods.
    // TODO: Get rid of this. It shouldn't exist in the first place.
    clientApi.cancelSubscription = () => {
      if (cancelSubscription) {
        return cancelSubscription();
      }

      // TODO#paywall: Remove this once MPP has been updated to support the new name
      if (updateRenewals) {
        return updateRenewals(false);
      }

      return Promise.reject(cantCallError);
    };

    clientApi.restoreSubscription = () => {
      if (restoreSubscription) {
        return restoreSubscription();
      }

      // TODO#paywall: Remove this once MPP has been updated to support the new name
      if (updateRenewals) {
        return updateRenewals(true);
      }

      return Promise.reject(cantCallError);
    };

    clientApi.changePlan = (newPlanId: string) => {
      if (changePlan) {
        return changePlan(newPlanId);
      }

      // TODO#paywall: Remove this once MPP has been updated to support the new name
      if (moveSubscription) {
        return moveSubscription(newPlanId);
      }

      return Promise.reject(cantCallError);
    };

    // Place to add custom properties to the API that has to be implemented no matter what.
    clientApi.hasOwnUi = api.hasOwnUi ?? (() => false);

    return clientApi;
  } catch (e) {
    console.error('Failed to initialize plugin API', e);

    return Promise.reject(cantCallError);
  }
};
